import { TWasmId } from "~/interfaces";
import Wasm from "../wasm";
import { IWasmStoreGeometry, TGeomVisualType } from "../store/interfaces";
import { getWasmIntercationType } from "../utils";
import { DEF_LINE_STYLES } from "../const";

type TVisualMapping = {
	[key in TGeomVisualType]?: TWasmId;
}

type TGeomDataType = "number" | "string";

export default class WasmMapGeometry {
	#wasmVisuals: TVisualMapping;

	#wasmIds: Array<TWasmId>;

	#wasm: Wasm;

	#data: IWasmStoreGeometry;

	#geomPoints: Array<number | string> | Array<Array<number | string>>;

	#geomType: TGeomDataType;

	// normal, active
	#zIndex: Array<number>;

	constructor(wasm: Wasm, data: IWasmStoreGeometry) {
		this.#wasmVisuals = {};
		this.#wasmIds = [];
		this.#wasm = wasm;
		this.#data = structuredClone(data);
		this.#geomPoints = [];
		this.#geomType = "number";
		this.#zIndex = [0, 0];

		if (this.#data.zIndex) {
			if (Array.isArray(this.#data.zIndex)) {
				this.#zIndex = this.#data.zIndex;
			} else {
				this.#zIndex = [this.#data.zIndex, this.#data.zIndex];
			}
		}

		this.#setGeomPointsFromData();
		this.#build();
		this.#updateVisual();
	}

	destroy() {
		for (const wasmId of this.#wasmIds) {
			this.#wasm.mapContent.remove(wasmId);
		}

		this.#wasm.entityChange();
	}

	updatePoints(points: IWasmStoreGeometry["points"]) {
		this.#data.points = points;

		this.#setGeomPointsFromData();
		this.#updateGeometry();
	}

	updateStrPoints(strPoints: IWasmStoreGeometry["strPoints"]) {
		this.#data.strPoints = strPoints;

		this.#setGeomPointsFromData();
		this.#updateGeometry();
	}

	setVisual(visual: TGeomVisualType) {
		this.#data.visual = visual;

		this.#updateVisual();
	}

	get wasmIds() {
		return this.#wasmIds;
	}

	setVisibility(visible: boolean) {
		this.#data.visible = visible;

		this.#updateVisual();
	}

	setBringToTheFront(bringToTheFront: boolean) {
		this.#data.bringToTheFront = bringToTheFront;

		const zIndex = this.#getZindex(bringToTheFront);

		this.#wasm.mapContent.setRenderLayer(this.#wasmIds, zIndex);
		this.#wasm.entityChange();
	}

	#build() {
		switch (this.#data.type) {
			case "measure-current": {
				const lineStyleActive = this.#wasm.createGeometryStyle(DEF_LINE_STYLES.MEASURE_CURRENT_ACTIVE);
				const lineStyleInactive = this.#wasm.createGeometryStyle(DEF_LINE_STYLES.MEASURE_CURRENT_INACTIVE);

				this.#wasmVisuals.standard = this.#addGeometry(lineStyleActive);
				this.#wasmVisuals.inactive = this.#addGeometry(lineStyleInactive);
				break;
			}

			case "measure-line": {
				const lineStyleActive = this.#wasm.createGeometryStyle(DEF_LINE_STYLES.MEASURE_LINE_ACTIVE);
				const lineStyleInactive = this.#wasm.createGeometryStyle(DEF_LINE_STYLES.MEASURE_LINE_INACTIVE);

				this.#wasmVisuals.standard = this.#addGeometry(lineStyleActive);
				this.#wasmVisuals.inactive = this.#addGeometry(lineStyleInactive);
				break;
			}

			case "activity-detail": {
				const lineStyleActive = this.#wasm.createGeometryStyle(DEF_LINE_STYLES.ACTIVITY_DETAIL);

				this.#wasmVisuals.standard = this.#addGeometry(lineStyleActive);
				break;
			}

			case "poi-geom": {
				const lineStyleActive = this.#wasm.createGeometryStyle(DEF_LINE_STYLES.POI_DETAIL_GEOM);

				this.#wasmVisuals.standard = this.#addGeometry(lineStyleActive);
				break;
			}

			case "backpack-route": {
				const lineStyleActive = this.#wasm.createGeometryStyle(DEF_LINE_STYLES.BACKPACK_PATH_ACTIVE);
				const lineStyleInactive = this.#wasm.createGeometryStyle(DEF_LINE_STYLES.BACKPACK_PATH_INACTIVE);

				this.#wasmVisuals.standard = this.#addGeometry(lineStyleActive);
				this.#wasmVisuals.inactive = this.#addGeometry(lineStyleInactive);
				break;
			}

			case "activity-lookup": {
				const lineStyleActive = this.#wasm.createGeometryStyle(DEF_LINE_STYLES.ACTIVITY_LOOKUP);

				this.#wasmVisuals.standard = this.#addGeometry(lineStyleActive);
				break;
			}

			default: {
				// default - zelena nebo mame custom a vlastni style
				if (this.#data.type === "custom" && this.#data.customStyleHash) {
					const variants = Object.keys(this.#data.customStyleHash) as Array<TGeomVisualType>;

					for (const variant of variants) {
						const variantStyle = this.#wasm.createGeometryStyle(this.#data.customStyleHash[variant]);

						this.#wasmVisuals[variant] = this.#addGeometry(variantStyle);
					}
				} else {
					const lineStyleActive = this.#wasm.createGeometryStyle(DEF_LINE_STYLES.DEFAULT);

					this.#wasmVisuals.standard = this.#addGeometry(lineStyleActive);
				}
				break;
			}
		}

		this.#wasmIds = [];

		const allVisuals = Object.keys(this.#wasmVisuals);

		for (const visual of allVisuals) {
			this.#wasmIds.push(this.#wasmVisuals[visual]);
		}

		this.#wasm.entityChange();
	}

	#addGeometry(lineStyle: Module.LineStyle) {
		if (!lineStyle) {
			/* eslint-disable-next-line */
			console.error(`Missing line style for type ${this.#data.type}!`);

			return -1;
		}

		const interactionType = getWasmIntercationType(this.#wasm.module, this.#data.interactionType);

		if (Array.isArray(this.#data.multilineData)) {
			return this.#wasm.mapContent.addMultiline(this.#geomPoints, this.#wasm.module.ObjectLayer.OverAll, this.#getZindex(this.#data.bringToTheFront), lineStyle, interactionType, this.#data.multilineData.map(item => ({
				...this.#data.customData,
				...item,
			})));
		}

		return this.#wasm.mapContent.addLine(this.#geomPoints, this.#wasm.module.ObjectLayer.OverAll, this.#getZindex(this.#data.bringToTheFront), lineStyle, interactionType, this.#data.customData);
	}

	#updateVisual() {
		const allVisuals = Object.keys(this.#wasmVisuals);
		const visibleIds: Array<TWasmId> = [];
		const hiddenIds: Array<TWasmId> = [];
		let curVisual = this.#data.visual;

		if (!(curVisual in this.#wasmVisuals)) {
			curVisual = "standard";
		}

		for (const visual of allVisuals) {
			const wasmId = this.#wasmVisuals[visual];

			if (this.#data.visible) {
				if (curVisual === visual) {
					visibleIds.push(wasmId);
				} else {
					hiddenIds.push(wasmId);
				}
			} else {
				hiddenIds.push(wasmId);
			}
		}

		visibleIds.length > 0 && this.#wasm.mapContent.setIsVisible(visibleIds, true);
		hiddenIds.length > 0 && this.#wasm.mapContent.setIsVisible(hiddenIds, false);

		this.#wasm.entityChange();
	}

	#setGeomPointsFromData() {
		if (this.#data.strPoints.length) {
			this.#geomType = "string";
			this.#geomPoints = this.#data.strPoints;
		} else {
			this.#geomType = "number";
			this.#geomPoints = this.#data.points;
		}
	}

	#updateGeometry() {
		for (const wasmId of this.#wasmIds) {
			const updateData = this.#geomType === "string"
				? this.#geomPoints
				: {
					coordType: this.#wasm.module.CoordSystem.Wgs,
					data: this.#geomPoints,
				};

			// @ts-ignore
			this.#wasm.mapContent.updateLineData(wasmId, updateData);
		}

		this.#wasm.entityChange();
	}

	#getZindex(bringToTheFront?: boolean) {
		return this.#zIndex[bringToTheFront ? 1 : 0];
	}
}
